import { TooltipIcon } from "@solomon-labs/design/components/icons";
import {
    Text,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger
} from "@solomon-labs/design/components/ui";
import React from "react";

import { ToggleMenu, ToggleMenuItem } from "../../components/ui/toggle-menu";

import type { DistributionMode } from "./types";

interface DistributionModeFieldProps {
    value: DistributionMode;
    onChange: (mode: DistributionMode) => void;
    /** Override the "Stream Directly" toggle label. Defaults to the
     *  longer copy used by the YaaS apply form. */
    streamLabel?: string;
    /** Override the "Claim Rewards" toggle label. Defaults to the
     *  longer copy used by the YaaS apply form. */
    claimLabel?: string;
    /** Optional explainer copy for the Stream option. Rendered as a
     *  tooltip behind an info icon next to the label so the option
     *  stays compact while the rationale is still discoverable. */
    streamDescription?: string;
    /** Optional explainer copy for the Claim option. Surfaced via
     *  tooltip on hover of the info icon next to the label. */
    claimDescription?: string;
    /** ToggleMenu layout: `fill` stretches each item to share the row
     *  evenly (apply-form default); `fit` hugs each item to its
     *  content. */
    layout?: "fill" | "fit";
    /** ToggleMenu orientation. Defaults to `horizontal` (apply-form
     *  layout). Set `vertical` when the options are stacked rows
     *  (typically paired with `layout="fill"`). */
    orientation?: "horizontal" | "vertical";
    /** When `false`, hides the "Distribution (Select One)" label
     *  above the toggle. Useful when the surrounding context already
     *  identifies the field (e.g. a parent tab named "Distribution
     *  Mode"). Defaults to `true` to preserve the apply-form layout. */
    showLabel?: boolean;
    /** Placement of the small "Select one" hint:
     *    - `"above"`: above the toggle, left-aligned.
     *    - `"below-right"`: below the toggle, right-aligned.
     *    - `"none"`: hide the hint entirely.
     *  Default tracks `showLabel`: when the body-variant label is
     *  shown it serves the role and the hint defaults to `"none"`;
     *  with `showLabel={false}` the hint defaults to `"above"`
     *  (current label-replacement behavior). */
    selectOneHint?: "above" | "below-right" | "none";
    /** Demo-only override that forces the Stream toggle item's hover /
     *  pressed VISUAL on with no pointer present — used by the scripted
     *  interaction sims to show a hover/click beat on the mode buttons.
     *  Forwards to the item's `forceHover` / `forcePressed`. Default
     *  `undefined`/idle — production and all other callers pass nothing
     *  and are unaffected. */
    demoStreamState?: "idle" | "hover" | "press";
    /** Demo-only override that forces the Claim toggle item's hover /
     *  pressed VISUAL on. Mirror of `demoStreamState` for the Claim
     *  option. Default `undefined`/idle. */
    demoClaimState?: "idle" | "hover" | "press";
}

/**
 * Distribution Mode field — single-select between Stream Directly
 * and Claim Rewards. Built on the shared `ToggleMenu` primitive
 * with `Button variant="toggle"` items (Figma node 2058:9078).
 *
 * Empty `onChange(null)` from a deselect is ignored — the field
 * always has exactly one mode selected.
 */
export function DistributionModeField({
    value,
    onChange,
    streamLabel = "Stream Directly",
    claimLabel = "Claim Rewards",
    streamDescription,
    claimDescription,
    layout = "fill",
    orientation = "horizontal",
    showLabel = true,
    selectOneHint,
    demoStreamState,
    demoClaimState
}: DistributionModeFieldProps): React.ReactElement {
    const hintPosition: "above" | "below-right" | "none" =
        selectOneHint ?? (showLabel ? "none" : "above");
    const renderItemContent = (
        label: string,
        description: string | undefined
    ): React.ReactNode =>
        description ? (
            // Label + info icon. The icon is a TooltipTrigger so
            // hovering it reveals the rationale on top side; the
            // toggle button stays at its compact label-only size.
            <span className="inline-flex items-center gap-1">
                {label}
                <Tooltip>
                    <TooltipTrigger render={<span className="text-current" />}>
                        <TooltipIcon className="size-5" />
                    </TooltipTrigger>
                    <TooltipContent side="top">{description}</TooltipContent>
                </Tooltip>
            </span>
        ) : (
            label
        );

    return (
        <div className="flex flex-col gap-3">
            {showLabel && (
                <Text
                    variant="body"
                    render={<span />}
                    className="text-foreground-base"
                >
                    Distribution (Select One)
                </Text>
            )}
            {/* "Select one" hint position is controlled by
                `selectOneHint`. Above: left-aligned gap-1 above the
                toggle (label-replacement default). Below-right:
                right-aligned mt-1 below the toggle (used by the
                Switch Distribution form where the H1 + divider
                identify the field). None: hide entirely. */}
            <div className="flex flex-col gap-1">
                {hintPosition === "above" && (
                    <Text
                        variant="small"
                        render={<span />}
                        className="text-foreground-neutral"
                    >
                        Select one
                    </Text>
                )}
                <TooltipProvider>
                    <ToggleMenu<DistributionMode>
                        select="single"
                        value={value}
                        onChange={(next) => {
                            if (next) onChange(next);
                        }}
                        spacing={2}
                        layout={layout}
                        orientation={orientation}
                    >
                        <ToggleMenuItem
                            value="stream"
                            variant="toggle"
                            size="md"
                            data-sim-cursor-key="mode-stream"
                            forceHover={demoStreamState === "hover"}
                            forcePressed={demoStreamState === "press"}
                        >
                            {renderItemContent(streamLabel, streamDescription)}
                        </ToggleMenuItem>
                        <ToggleMenuItem
                            value="claim"
                            variant="toggle"
                            size="md"
                            data-sim-cursor-key="mode-claim"
                            forceHover={demoClaimState === "hover"}
                            forcePressed={demoClaimState === "press"}
                        >
                            {renderItemContent(claimLabel, claimDescription)}
                        </ToggleMenuItem>
                    </ToggleMenu>
                </TooltipProvider>
                {hintPosition === "below-right" && (
                    <Text
                        variant="small"
                        render={<span />}
                        className="text-foreground-neutral text-right mt-1"
                    >
                        Select one
                    </Text>
                )}
            </div>
        </div>
    );
}
